/**
 * K 線元件 — klinecharts v9 版（從 lightweight-charts 重寫）。
 *
 * 為何換：lightweight-charts 的 Y 軸 priceScale scaleMargins / autoscaleInfoProvider
 * 邊距處理太繁，反覆改不直觀。klinecharts 對 K 線特化，Y 軸 / priceLine /
 * volume / 技術指標都是原生概念，不用玩底層 autoscale 數學。
 *
 * 對外 Props 維持跟舊版相容（candles / intraday / timezone / priceLines / symbol / sessionOpenTs）。
 */
import { useEffect, useRef, useState } from 'react';
import {
  init,
  dispose,
  LineType,
  CandleType,
  TooltipShowRule,
  ActionType,
  registerOverlay,
  registerIndicator,
  IndicatorSeries,
  type Chart,
  type KLineData,
  type Crosshair,
  type Indicator,
  type IndicatorFigureStylesCallbackData,
  type OverlayCreateFiguresCallbackParams,
  type OverlayFigure,
} from 'klinecharts';
import type { Candle } from '@invest-copilot/shared';

export interface PriceLineDef {
  /** 唯一 key */
  key: string;
  price: number;
  /** 顯示在線段右邊的標題 */
  title: string;
  /** hex color */
  color: string;
  /** 0 solid, 1 dotted, 2 dashed, 3 large dashed, 4 sparse dotted (跟舊版相容) */
  lineStyle?: 0 | 1 | 2 | 3 | 4;
  lineWidth?: 1 | 2 | 3 | 4;
  /** 是否將這條線的價格納入 Y 軸範圍計算。預設 true。 */
  includeInAutoscale?: boolean;
}

interface Props {
  candles: Candle[];
  intraday?: boolean;
  timezone?: string;
  priceLines?: PriceLineDef[];
  symbol?: string;
  /** 單日分時折線模式（上紅下綠、昨收基準）；只適用「單一交易日」（1m/5m，range=1d）。
   *  多日盤中（30m/60m，range=5d）與日線一律 false → 標準紅綠蜋燭。預設沿用 intraday。 */
  lineChart?: boolean;
  /** 昨收（算 OHLC strip 漲跌%的基準，與「是否畫昨收線」解耦） */
  prevClose?: number;
  /** ATR14（顯在 OHLC strip 末端；無值不顯） */
  atr14?: number;
  /** 今日正常交易時段開盤的 unix timestamp（秒），目前未使用（後端已 clip 範圍） */
  sessionOpenTs?: number;
}

const MA_SHORT_COLOR = '#38bdf8'; // 藍：MA5 / MA20，避開 K 線紅綠與 priceLine 顏色
const MA_LONG_COLOR = '#a78bfa';  // 紫
const PRICE_LINE_COLOR = '#f8fafc'; // 分時圖價格線：亮白，和 MA20 藍明顯區隔
const UP = '#ef5350';     // 台股慣例：漲紅
const DOWN = '#26a69a';   // 跌綠
const SOLID_LINE_STYLE = { style: LineType.Solid, dashedValue: [2, 2], smooth: false };
const Y_AXIS_WIDTH_ESTIMATE = 48;
const LEFT_TOOLTIP_GUTTER = 18;
const BASE_RIGHT_OFFSET = 28;
const MIN_RIGHT_OFFSET = 32;
const MAX_RIGHT_OFFSET = 180;
const PRICE_LINE_LABEL_PADDING = 18;
const LABELED_PRICE_LINE_OVERLAY = 'labeledPriceLine';

registerOverlay({
  name: LABELED_PRICE_LINE_OVERLAY,
  totalStep: 2,
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: false,
  createPointFigures: ({ coordinates, bounding, overlay }: OverlayCreateFiguresCallbackParams): OverlayFigure[] => {
    const yCoord = coordinates[0]?.y;
    if (!Number.isFinite(yCoord)) return [];
    const y = yCoord as number;
    const styles = overlay.styles as { line?: { color?: string; size?: number; style?: LineType } } | null;
    const color = styles?.line?.color ?? '#f8fafc';
    const label = typeof overlay.extendData === 'string' && overlay.extendData.trim().length > 0
      ? overlay.extendData
      : String(overlay.points[0]?.value ?? '');

    return [
      {
        type: 'line',
        attrs: { coordinates: [{ x: 0, y }, { x: bounding.width, y }] },
        styles: {
          color,
          size: styles?.line?.size ?? 1,
          style: styles?.line?.style ?? LineType.Solid,
          dashedValue: [4, 4],
        },
      },
      {
        type: 'text',
        ignoreEvent: true,
        attrs: {
          x: Math.max(8, bounding.width - 8),
          y: y - 4,
          text: label,
          align: 'right',
          baseline: 'bottom',
        },
        styles: {
          color,
          size: 11,
          weight: '700',
          backgroundColor: 'rgba(15, 17, 21, 0.82)',
          borderColor: color,
          borderSize: 1,
          borderRadius: 3,
          paddingLeft: 4,
          paddingRight: 4,
          paddingTop: 2,
          paddingBottom: 2,
        },
      },
    ];
  },
});

/**
 * 盤中分時自訂 indicator：以「昨收」為基準，畫面分上下兩半——
 * 收盤線高於昨收的區段畫紅（fill 紅）、低於昨收畫綠，正宗台股分時圖。
 * 用 canvas clip（上半 / 下半矩形）對「線到基準的填充多邊形」各裁切一次，
 * 不必手動內插交叉點就能精準分色。基準價 = 昨收 prevClose（由 indicator.extendData 帶入；
 * 拿不到昨收才退回第一根 open）。第一根（今日開盤）左側再 prepend 一個昨收錦點，
 * 讓折線第一段斜接「昨收 → 開盤」把隔夜跳空畫出來。
 * 預設 area 在 intraday 時被設成透明，由本 indicator 取代繪製。
 */
const INTRADAY_BASELINE_INDICATOR = 'intradayBaseline';
const BASE_UP_RGB = '239, 83, 80';   // 紅（高於開盤）
const BASE_DOWN_RGB = '38, 166, 154'; // 綠（低於開盤）

registerIndicator({
  name: INTRADAY_BASELINE_INDICATOR,
  shortName: '',
  shouldOhlc: false,
  figures: [],
  calc: (dataList) => dataList.map(() => ({})),
  draw: ({ ctx, kLineDataList, indicator, visibleRange, bounding, xAxis, yAxis }) => {
    const ext = indicator.extendData as { prevClose?: number } | null;
    const prevClose = ext?.prevClose;
    const usePrevClose = typeof prevClose === 'number' && Number.isFinite(prevClose) && prevClose > 0;
    // 基準價＝昨收（高於昨收紅、低於昨收綠）；拿不到昨收才退回第一根開盤。
    const baseline = usePrevClose ? (prevClose as number) : kLineDataList[0]?.open;
    if (baseline == null || !Number.isFinite(baseline)) return false;
    const yBase = yAxis.convertToPixel(baseline);
    const paneBottom = bounding.height;
    if (!Number.isFinite(yBase)) return true;

    const pts: Array<{ x: number; y: number }> = [];
    for (let i = visibleRange.from; i < visibleRange.to; i++) {
      const d = kLineDataList[i];
      if (!d || !Number.isFinite(d.close)) continue;
      const x = xAxis.convertToPixel(i);
      const y = yAxis.convertToPixel(d.close);
      if (Number.isFinite(x) && Number.isFinite(y)) pts.push({ x, y });
    }
    if (pts.length < 2) return true;

    // 第一個起點＝昨收→開盤：看得到今日第一根（from===0）且有昨收時，於最左 prepend 一個「昨收」錦點（y＝yBase），
    // 讓折線第一段斜接「昨收 → 開盤後第一根」，把隔夜跳空畫出來。
    if (usePrevClose && visibleRange.from === 0) {
      const x0 = xAxis.convertToPixel(0);
      const x1 = xAxis.convertToPixel(1);
      const barW = Number.isFinite(x0) && Number.isFinite(x1) ? Math.max(2, x1 - x0) : 6;
      if (Number.isFinite(x0)) pts.unshift({ x: x0 - barW, y: yBase });
    }

    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    const fillToBaseline = () => {
      ctx.beginPath();
      ctx.moveTo(first.x, yBase);
      for (const p of pts) ctx.lineTo(p.x, p.y);
      ctx.lineTo(last.x, yBase);
      ctx.closePath();
    };
    const strokeLine = (color: string) => {
      ctx.beginPath();
      ctx.moveTo(first.x, first.y);
      for (const p of pts) ctx.lineTo(p.x, p.y);
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.lineJoin = 'round';
      ctx.stroke();
    };
    const clipRect = (top: number, height: number, paint: () => void) => {
      if (height <= 0) return;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, top, bounding.width, height);
      ctx.clip();
      paint();
      ctx.restore();
    };

    // 上半（高於開盤）→ 紅；下半（低於開盤）→ 綠
    clipRect(0, yBase, () => { fillToBaseline(); ctx.fillStyle = `rgba(${BASE_UP_RGB}, 0.16)`; ctx.fill(); });
    clipRect(yBase, paneBottom - yBase, () => { fillToBaseline(); ctx.fillStyle = `rgba(${BASE_DOWN_RGB}, 0.16)`; ctx.fill(); });
    clipRect(0, yBase, () => strokeLine(`rgb(${BASE_UP_RGB})`));
    clipRect(yBase, paneBottom - yBase, () => strokeLine(`rgb(${BASE_DOWN_RGB})`));

    // 開盤垂直標記線（最左＝開盤）
    const x0 = xAxis.convertToPixel(0);
    if (Number.isFinite(x0) && x0 >= 0 && x0 <= bounding.width) {
      ctx.save();
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x0, 0);
      ctx.lineTo(x0, paneBottom);
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.55)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.restore();
    }
    return true;
  },
});

/**
 * 跨日分隔線 indicator（只用於多日盤中：30分/60分，range=5d 橫跨多個交易日）。
 * 在「日期相對前一根改變」的那根畫一條垂直虛線 + 頂部琥珀色「MM/DD」標籤，
 * 讓使用者一眼看出哪裡換日（解決「日期/時間字體顏色都一樣、看不出不同天」）。
 * 日界以圖表時區（timezone，由 extendData 帶入；台股 Asia/Taipei）計算，避免 UTC 換日點偏移。
 */
const DAY_SEPARATOR_INDICATOR = 'daySeparator';
const DAY_SEP_LINE_COLOR = 'rgba(148, 163, 184, 0.45)'; // 灰藍虛線
const DAY_SEP_TAG_BG = 'rgba(251, 191, 36, 0.92)';       // 琥珀標籤底
const DAY_SEP_TAG_FG = '#1a1a1a';                         // 深色字

registerIndicator({
  name: DAY_SEPARATOR_INDICATOR,
  shortName: '',
  shouldOhlc: false,
  figures: [],
  calc: (dataList) => dataList.map(() => ({})),
  draw: ({ ctx, kLineDataList, indicator, visibleRange, bounding, xAxis }) => {
    const ext = indicator.extendData as { timezone?: string } | null;
    let fmt: Intl.DateTimeFormat;
    try {
      fmt = new Intl.DateTimeFormat('en-CA', { timeZone: ext?.timezone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      fmt = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }
    const dayOf = (tsMs: number) => fmt.format(new Date(tsMs)); // YYYY-MM-DD（圖表時區）
    ctx.save();
    ctx.textBaseline = 'top';
    ctx.textAlign = 'left';
    ctx.font = '700 10px -apple-system, "Segoe UI", system-ui, sans-serif';
    for (let i = Math.max(1, visibleRange.from); i < visibleRange.to; i++) {
      const cur = kLineDataList[i];
      const prev = kLineDataList[i - 1];
      if (!cur || !prev) continue;
      if (dayOf(cur.timestamp) === dayOf(prev.timestamp)) continue; // 同一天，不畫
      const x = xAxis.convertToPixel(i);
      if (!Number.isFinite(x)) continue;
      // 垂直分隔虛線
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, bounding.height);
      ctx.strokeStyle = DAY_SEP_LINE_COLOR;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.setLineDash([]);
      // 頂部「MM/DD」標籤
      const label = dayOf(cur.timestamp).slice(5).replace('-', '/'); // 06-09 → 06/09
      const tw = ctx.measureText(label).width;
      const padX = 3;
      const bx = x + 2;
      ctx.fillStyle = DAY_SEP_TAG_BG;
      ctx.fillRect(bx, 1, tw + padX * 2, 14);
      ctx.fillStyle = DAY_SEP_TAG_FG;
      ctx.fillText(label, bx + padX, 3);
    }
    ctx.restore();
    return true;
  },
});

/**
 * 自訂成交量 indicator：盤中分時 volume bar 按「收盤 vs 昨收」分紅綠（與分時線上紅下綠一致），
 * 日線退回「收盤 vs 開盤」（一般慣例）。保留 VOL MA5/10/20 線。prevClose / intraday 由 extendData 帶入。
 */
const VOL_BASELINE_INDICATOR = 'VOL_BASELINE';
const VOL_UP = 'rgba(239, 83, 80, 0.7)';   // 紅（高於基準）
const VOL_DOWN = 'rgba(38, 166, 154, 0.7)'; // 綠（低於基準）
const VOL_MA_PERIODS = [5, 10, 20] as const;
const VOL_MA_KEYS = ['ma5', 'ma10', 'ma20'] as const;
interface VolData { volume: number; ma5?: number; ma10?: number; ma20?: number }

registerIndicator<VolData>({
  name: VOL_BASELINE_INDICATOR,
  shortName: 'VOL',
  series: IndicatorSeries.Volume,
  shouldFormatBigNumber: true,
  calcParams: [...VOL_MA_PERIODS],
  figures: [
    { key: 'ma5', title: 'MA5: ', type: 'line' },
    { key: 'ma10', title: 'MA10: ', type: 'line' },
    { key: 'ma20', title: 'MA20: ', type: 'line' },
    {
      key: 'volume',
      title: 'VOL: ',
      type: 'bar',
      baseValue: 0,
      styles: (data: IndicatorFigureStylesCallbackData<VolData>, indicator: Indicator<VolData>) => {
        const kline = data.current?.kLineData;
        const ext = indicator.extendData as { prevClose?: number; intraday?: boolean } | null;
        const close = kline?.close;
        let up: boolean;
        if (ext?.intraday && typeof ext.prevClose === 'number' && Number.isFinite(ext.prevClose) && ext.prevClose > 0) {
          up = typeof close === 'number' ? close >= ext.prevClose : true;
        } else {
          const open = kline?.open;
          up = typeof close === 'number' && typeof open === 'number' ? close >= open : true;
        }
        return { color: up ? VOL_UP : VOL_DOWN };
      },
    },
  ],
  calc: (dataList: KLineData[]): VolData[] => {
    const sums = VOL_MA_PERIODS.map(() => 0);
    return dataList.map((kline, i) => {
      const volume = kline.volume ?? 0;
      const result: VolData = { volume };
      VOL_MA_PERIODS.forEach((p, idx) => {
        sums[idx]! += volume;
        if (i >= p) sums[idx]! -= dataList[i - p]?.volume ?? 0;
        if (i >= p - 1) result[VOL_MA_KEYS[idx]!] = sums[idx]! / p;
      });
      return result;
    });
  },
});

/**
 * 分時（1m/5m）下方 pane＝MACD（12/26/9）柱狀圖（2026-06-10，對齊使用者給的 us-stocks MACD 參考）。
 * **為何從「發散式成交量」改成 MACD**：使用者要的「綠轉紅前慢慢往下、紅慢慢往上」漸進過零軸效果，
 *   是 MACD 柱（畫平滑震盪值 Hist＝DIF−DEA）的本質——Hist 會慢慢趨近 0 再反向長大。成交量做不到：
 *   量柱高度＝量、紅綠＝逐根方向，轉折時量還很大，只有正負號瞬間翻 → 「瞬間就相反」（使用者點出的問題）。
 * **定義**（與參考圖一致：MACD -0.187 / Sig -0.050 / Hist -0.137，Hist＝DIF−DEA）：
 *   DIF（參考標「MACD」）＝EMA12−EMA26；DEA（參考標「Sig」）＝EMA9(DIF)；Hist＝DIF−DEA。
 * **顏色（台股）**：Hist ≥ 0 紅（動能轉多 / 買盤）往上、< 0 綠（動能轉空 / 賣壓）往下；DIF 藍線、DEA 橘線。
 * **連續直方柱**：klinecharts 原生 bar 寬只有 `halfGapBar*2`（間隙大像柵欄），改自訂 `draw` 畫滿格寬
 *   `barSpace.bar`（相鄰相連）。`figures` 保留 dif/dea/macd 三個 key 以驅動 Y 軸範圍；`draw` 回傳 `true`
 *   ＝isCover，跳過預設繪製、改由 draw 畫連續柱 + 兩條線。不設 Volume series，用 Normal series 讓 Y 軸含負值。
 */
const MACD_DIVERGE_INDICATOR = 'MACD_DIVERGE';
const MACD_DIF_COLOR = '#4fc3f7'; // DIF（「MACD」線）淡藍
const MACD_DEA_COLOR = '#ff9e54'; // DEA（「Sig」訊號線）橘
interface MacdData { dif: number; dea: number; macd: number }

/** 指數移動平均序列；以第一筆當種子（盤中根數有限，視覺夠用）。 */
function emaSeries(values: number[], period: number): number[] {
  const k = 2 / (period + 1);
  const out: number[] = [];
  let prev = values[0] ?? 0;
  for (let i = 0; i < values.length; i++) {
    prev = i === 0 ? values[i]! : values[i]! * k + prev * (1 - k);
    out.push(prev);
  }
  return out;
}

registerIndicator<MacdData>({
  name: MACD_DIVERGE_INDICATOR,
  shortName: 'MACD',
  precision: 4,
  // 三個 key 驅動 Y 軸範圍（含 DIF/DEA/Hist）；實際繪製由 draw 取代（回傳 true 蓋掉）。
  figures: [
    { key: 'dif', title: 'DIF: ', type: 'line' },
    { key: 'dea', title: 'DEA: ', type: 'line' },
    { key: 'macd', title: 'MACD: ', type: 'bar', baseValue: 0 },
  ],
  calc: (dataList: KLineData[]): MacdData[] => {
    const closes = dataList.map((k) => k.close);
    const ema12 = emaSeries(closes, 12);
    const ema26 = emaSeries(closes, 26);
    const dif = closes.map((_, i) => ema12[i]! - ema26[i]!);
    const dea = emaSeries(dif, 9);
    return dataList.map((_, i) => ({ dif: dif[i]!, dea: dea[i]!, macd: dif[i]! - dea[i]! }));
  },
  draw: ({ ctx, indicator, visibleRange, barSpace, xAxis, yAxis }) => {
    const result = indicator.result as MacdData[];
    const yZero = yAxis.convertToPixel(0);
    // 柱寬留約 10% 間距（不像原生 halfGapBar*2 那麼疏、也不像全格寬 barSpace.bar 黏死）：
    // 使用者要「稍微有點間距」。用比例而非固定 px → 任何縮放都維持一致的細縫、不會放大時全黏。
    const w = Math.max(1, barSpace.bar * 0.9);
    const { from, to } = visibleRange;
    ctx.save();
    // (1) Hist 連續直方柱：≥0 紅往上、<0 綠往下。柱高隨 Hist 大小，過零軸時自然縮到 0 → 漸進反轉。
    for (let i = from; i < to; i++) {
      const h = result[i]?.macd;
      if (h == null || !Number.isFinite(h) || h === 0) continue;
      const x = xAxis.convertToPixel(i);
      if (!Number.isFinite(x)) continue;
      const y = yAxis.convertToPixel(h);
      const top = Math.min(yZero, y);
      const ht = Math.max(1, Math.abs(y - yZero));
      ctx.fillStyle = h >= 0 ? VOL_UP : VOL_DOWN;
      ctx.fillRect(x - w / 2, top, w, ht);
    }
    // (2) DIF / DEA 線
    const drawLine = (key: 'dif' | 'dea', color: string) => {
      ctx.beginPath();
      let started = false;
      for (let i = from; i < to; i++) {
        const v = result[i]?.[key];
        if (v == null || !Number.isFinite(v)) continue;
        const x = xAxis.convertToPixel(i);
        const y = yAxis.convertToPixel(v);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
      }
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.stroke();
    };
    drawLine('dif', MACD_DIF_COLOR);
    drawLine('dea', MACD_DEA_COLOR);
    ctx.restore();
    return true; // isCover：跳過預設 figure 繪製
  },
});

/** 下方 pane 換 indicator：單日折線(isLine) → MACD 柱、多日蜡燭/日線 → 標準量柱。idempotent。 */
function applyVolumeIndicator(chart: Chart, isLine: boolean, prevClose?: number): void {
  chart.removeIndicator('volume_pane', VOL_BASELINE_INDICATOR);
  chart.removeIndicator('volume_pane', MACD_DIVERGE_INDICATOR);
  if (isLine) {
    // MACD 不需 prevClose / extendData（畫平滑震盪值，與昨收無關）。
    chart.createIndicator({ name: MACD_DIVERGE_INDICATOR }, false, { id: 'volume_pane', height: 80 });
  } else {
    chart.createIndicator({ name: VOL_BASELINE_INDICATOR, extendData: { prevClose, intraday: isLine } }, false, { id: 'volume_pane', height: 80 });
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function estimateLabelWidthPx(text: string): number {
  let width = 0;
  for (const ch of text) {
    // CJK 字寬接近 11px；ASCII/數字約 6px。只需估計 gutter，不必做到 canvas measureText。
    width += ch.charCodeAt(0) > 255 ? 11 : 6;
  }
  return Math.ceil(width);
}

function estimatePriceLineLabelGutter(priceLines?: PriceLineDef[]): number {
  const labels = (priceLines ?? [])
    .map((p) => p.title?.trim())
    .filter((title): title is string => !!title);
  if (labels.length === 0) return 0;
  const labelWidth = Math.max(...labels.map(estimateLabelWidthPx)) + PRICE_LINE_LABEL_PADDING;
  return clamp(labelWidth, 72, 150);
}

function resetRightOffset(containerWidth: number, priceLines?: PriceLineDef[]): number {
  const labelGutter = estimatePriceLineLabelGutter(priceLines);
  const baseOffset = Math.round(containerWidth * 0.025) + BASE_RIGHT_OFFSET;
  const preferred = labelGutter > 0 ? Math.max(baseOffset, labelGutter) : baseOffset;
  // 窄螢幕不能讓右側標籤吃掉半張圖；桌面則最多保留 180px 給完整語意標籤。
  const dynamicMax = Math.max(MIN_RIGHT_OFFSET, Math.min(MAX_RIGHT_OFFSET, containerWidth * 0.22));
  return Math.round(clamp(preferred, MIN_RIGHT_OFFSET, dynamicMax));
}

function resetBarSpace(dataCount: number, containerWidth: number, priceLines?: PriceLineDef[]): number {
  const mainWidth = Math.max(160, containerWidth - Y_AXIS_WIDTH_ESTIMATE);
  const rightOffset = resetRightOffset(containerWidth, priceLines);
  // 價格線標籤已移到右側，所以 reset/fit 時只在左邊保留很小的邊界，
  // 主要空間改留給右側完整語意標籤，避免最新 K 線被標籤壓住。
  const availableWidth = Math.max(80, mainWidth - rightOffset - LEFT_TOOLTIP_GUTTER);
  const fitSpace = availableWidth / Math.max(1, dataCount);
  // klinecharts v9 clamps internally to 1..50; clamp here so reset is deterministic.
  return clamp(fitSpace, 1, 50);
}

function ksLineStyle(s?: number): LineType {
  return s === 0 ? LineType.Solid : LineType.Dashed;
}

/**
 * 直接把 klinecharts 容器內每個 canvas 的 buffer（canvas.width/height 屬性）設成 CSS×DPR，繞過 klinecharts
 * 失效的自動 sizing。
 *
 * 根因（讀 node_modules/klinecharts@9.8.12 的 `Canvas` 類）：在「支援 device-pixel-content-box」的瀏覽器
 * （現代 Chrome），canvas buffer **只靠一個 device-pixel-content-box ResizeObserver 的回調設定**（`Canvas.update`
 * 在 supported 時只設 CSS style、不主動設 buffer）。該 observer 由非同步 `isSupportedDevicePixelContentBox()`
 * 的 Promise resolve 後才 `observe`，在 work-grid 首次 mount 的時序下初始回調常沒 fire → buffer 永遠卡 HTML
 * 預設 **300×150**、CSS 卻是容器尺寸（如 748×215）→ 瀏覽器把 300×150 非等比拉伸到 CSS 尺寸 = user 看到的
 * 「K 線 / 字體一打開就被拉高」。`chart.resize()` 只重算 pane 的 CSS、不碰 buffer；window resize / 改容器尺寸 /
 * 改 canvas style 都無法喚醒那個壞掉的 observer（實測 buffer 永遠 300×150）。
 *
 * 解法：klinecharts 已把每個 canvas 的 CSS style.width/height 算對了，直接讀它 × DPR 設成 buffer attribute，
 * DPR≠1 時補 `ctx.setTransform(dpr,…)`（設 canvas.width 會 reset transform，需重設縮放）；再 `chart.resize()`
 * 觸發 klinecharts 用正確 buffer 完整重繪。idempotent：buffer 已對就跳過（不干擾 klinecharts 正常設好 buffer 的情況）。
 */
function fixCanvasBuffers(chart: Chart, container: HTMLElement | null): void {
  if (!container) return;
  const dpr = window.devicePixelRatio || 1;
  let fixed = false;
  container.querySelectorAll('canvas').forEach((c) => {
    const w = parseFloat(c.style.width || '0');
    const h = parseFloat(c.style.height || '0');
    if (!(w > 0 && h > 0)) return; // klinecharts 還沒設 CSS 尺寸，等下個 settle 時間點
    const tw = Math.round(w * dpr);
    const th = Math.round(h * dpr);
    if (c.width !== tw || c.height !== th) {
      c.width = tw;
      c.height = th;
      const ctx = c.getContext('2d');
      if (ctx && dpr !== 1) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fixed = true;
    }
  });
  if (fixed) chart.resize(); // 觸發 klinecharts 用正確 buffer 完整重繪
}

/**
 * 多階段「沉澱後再修 buffer」：接住「init / 切股票 / 切時間框當下 layout 尚未穩定、klinecharts canvas buffer
 * 沒對齊容器」的 race（症狀：K 線 / 字體被垂直拉高、十字 Y 對不準）。每個時間點先 `chart.resize()`（讓 klinecharts
 * 把 canvas CSS 尺寸算對）再 `fixCanvasBuffers`（手動把 buffer 設成 CSS×DPR + 重繪）。rAF + 150/450/900ms 各補一次。
 * ⚠️ 必須同時用於「init」與「data-sync 的 resetViewport」兩條路徑：切股票 / 切時間框走的是 data-sync（不重新 init）。
 */
function scheduleSettleResize(chart: Chart, container: HTMLElement | null, isValid: () => boolean): void {
  const fix = () => {
    if (!isValid()) return;
    chart.resize();
    fixCanvasBuffers(chart, container);
  };
  requestAnimationFrame(fix);
  setTimeout(fix, 150);
  setTimeout(fix, 450);
  setTimeout(fix, 900);
}

/**
 * 削掉「不可能的影線」壞 tick：某些盤後/盤前幽靈 bar 的 high/low 與自身實體完全脫節
 * （實測 MU 30m 出現 O/L/C~860 但 high=1688、V=0 的壞點），會把 Y 軸 autoscale 搐爆、
 * 真實 K 線被壓成一條。判定：影線長 > 實體另一側整體範圍的 5 倍、且 > 收盤價 5%（雙條件
 * 避免把正常大波動 bar 誤殺）→ 把該影線削回實體邊界（只動 high/low、不動實體 open/close）。
 */
function sanitizeWicks(open: number, high: number, low: number, close: number): { high: number; low: number } {
  const bodyTop = Math.max(open, close);
  const bodyBottom = Math.min(open, close);
  const upperWick = high - bodyTop;
  const lowerWick = bodyBottom - low;
  const belowSpan = Math.max(bodyTop - low, 1e-9);    // 上影線以外（實體+下影）總範圍
  const aboveSpan = Math.max(high - bodyBottom, 1e-9); // 下影線以外（實體+上影）總範圍
  const absFloor = Math.abs(close) * 0.05;
  const badUpper = upperWick > 5 * belowSpan && upperWick > absFloor;
  const badLower = lowerWick > 5 * aboveSpan && lowerWick > absFloor;
  return {
    high: badUpper ? bodyTop : high,
    low: badLower ? bodyBottom : low,
  };
}

function toKLineData(candles: Candle[]): KLineData[] {
  const out: KLineData[] = [];
  for (const c of candles) {
    if (
      !Number.isFinite(c.open) || !Number.isFinite(c.high) ||
      !Number.isFinite(c.low) || !Number.isFinite(c.close) ||
      !Number.isFinite(c.timestamp)
    ) continue;
    const { high, low } = sanitizeWicks(c.open, c.high, c.low, c.close);
    out.push({
      timestamp: c.timestamp * 1000, // klinecharts 用毫秒
      open: c.open,
      high,
      low,
      close: c.close,
      volume: c.volume,
    });
  }
  return out;
}

export function CandleChart({ candles, intraday = false, lineChart, timezone, priceLines, symbol, prevClose, atr14, sessionOpenTs }: Props) {
  // 單日分時折線模式：1m/5m（單一交易日）才畫上紅下綠分時折線；30m/60m（多日 5d）與日線用標準蜋燭。
  // 不傳時沿用 intraday（向後相容）。
  const isLine = lineChart ?? intraday;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const lastSymbolRef = useRef<string | undefined>(undefined);
  const lastIntradayRef = useRef<boolean>(intraday);
  const lastCandleCountRef = useRef<number>(0);
  const lastCandleTsRef = useRef<number>(0);
  const lastFirstCandleTsRef = useRef<number>(0);
  const lastRightOffsetRef = useRef<number>(0);
  const priceLinesRef = useRef<PriceLineDef[] | undefined>(priceLines);
  /** priceLine overlay id 對照 */
  const overlayIdsRef = useRef<Map<string, string>>(new Map());
  /**
   * chart 是否真的 init 成功（container 必須有非零寬度才能 init）。
   * 用 state 而非 ref，讓下游 data / priceLines effect 在 chart ready 後自動 re-run。
   */
  const [chartReady, setChartReady] = useState(false);
  /** 游標 hover 到的那根 K（離開時 null → 外部 OHLC strip 回顯最新棒）。 */
  const [hoverBar, setHoverBar] = useState<KLineData | null>(null);
  priceLinesRef.current = priceLines;

  // === init once，但要等 container 有寬度才真正 init（避免 width=0 狀態壞掉）===
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let chart: Chart | null = null;
    let cancelled = false;

    const tryInit = () => {
      // 容器需同時有寬度也有高度才 init：只驗 clientWidth 時，若 clientHeight 暫為 0（layout 未定），
      // klinecharts 會把 canvas buffer 高度設成預設、之後只設 CSS 高度 → 內容被垂直拉高、十字歪。
      if (cancelled || chart || container.clientWidth === 0 || container.clientHeight === 0) return;
      const c = init(container);
      if (!c) return;
      chart = c;
      chartRef.current = c;
      c.resize();
      c.setZoomEnabled(true);
      c.setScrollEnabled(true);

      c.setStyles({
        grid: {
          show: true,
          horizontal: { show: true, color: 'rgba(42, 45, 52, 0.5)', style: LineType.Dashed },
          vertical: { show: true, color: 'rgba(42, 45, 52, 0.5)', style: LineType.Dashed },
        },
        candle: {
          type: CandleType.CandleSolid,
          area: {
            lineColor: PRICE_LINE_COLOR,
            lineSize: 2,
            backgroundColor: [
              { offset: 0, color: 'rgba(248, 250, 252, 0.14)' },
              { offset: 1, color: 'rgba(248, 250, 252, 0)' },
            ],
          },
          bar: {
            upColor: UP,
            downColor: DOWN,
            upBorderColor: UP,
            downBorderColor: DOWN,
            upWickColor: UP,
            downWickColor: DOWN,
          },
          priceMark: {
            high: { show: true, color: '#9aa0a6' },
            low: { show: true, color: '#9aa0a6' },
            last: { show: true },
          },
          tooltip: {
            // 畫布上不畫 OHLC legend（會擋住 K 線本體）。所有 O/H/L/C/漲跌/量 已独立出畫布外
            // 的 OHLC strip（預設顯最新棒，hover 同步該棒）——資訊永遠在、但不蓋圖。
            showRule: TooltipShowRule.None,
          },
        },
        xAxis: {
          axisLine: { color: '#2a2d34' },
          tickText: { color: '#9aa0a6' },
          tickLine: { color: '#2a2d34' },
        },
        yAxis: {
          axisLine: { color: '#2a2d34' },
          tickText: { color: '#9aa0a6' },
          tickLine: { color: '#2a2d34' },
        },
        crosshair: {
          horizontal: { line: { color: 'rgba(79, 195, 247, 0.4)' } },
          vertical: { line: { color: 'rgba(79, 195, 247, 0.4)' } },
        },
        indicator: {
          // MA(5,20) / VOL(5,10,20) legend 同樣不畫在畫布上（MA 顏色圖例已在畫布外 strip）。
          tooltip: { showRule: TooltipShowRule.None },
        },
      });

      if (timezone) c.setTimezone(timezone);

      // MA indicator on main pane
      const maParams = intraday ? [5, 20] : [20, 60];
      c.createIndicator(
        {
          name: 'MA',
          calcParams: maParams,
          styles: {
            lines: [
              { ...SOLID_LINE_STYLE, color: MA_SHORT_COLOR, size: 2 },
              { ...SOLID_LINE_STYLE, color: MA_LONG_COLOR, size: 2 },
            ],
          },
        } as Parameters<Chart['createIndicator']>[0],
        true,
        { id: 'candle_pane' },
      );

      // VOL indicator on separate pane（單日折線 → 發散式上紅下綠；多日蜡燭/日線 → 標準量柱）
      applyVolumeIndicator(c, isLine, prevClose);

      // 游標 hover → 更新畫布外 OHLC strip 顯示該棒；離開（無 kLineData）→ 回顯最新棒。
      c.subscribeAction(ActionType.OnCrosshairChange, (data?: Crosshair) => {
        setHoverBar(data?.kLineData ?? null);
      });

      const initialRightOffset = resetRightOffset(container.clientWidth || 800, priceLinesRef.current);
      c.setOffsetRightDistance(initialRightOffset);
      lastRightOffsetRef.current = initialRightOffset;
      c.resize();
      // 多階段補 resize：接住「init 當下 layout 尚未定案 → canvas buffer 沒對齊容器 → 內容被非等比
      // 拉伸」的 race（字體垂直拉高 / K 線出界 / 十字 Y 歪）。見 scheduleSettleResize 註解。
      scheduleSettleResize(c, container, () => !cancelled && chart === c);
      setChartReady(true);
    };

    // 立刻嘗試 init（container 已掛載 + 父層有寬度時會成功）
    tryInit();

    // ResizeObserver：第一次有寬度時 init，後續純粹 resize
    const ro = new ResizeObserver(() => {
      if (cancelled) return;
      if (!chart) tryInit();
      else chart.resize();
    });
    ro.observe(container);

    return () => {
      cancelled = true;
      ro.disconnect();
      if (chart) dispose(container);
      chartRef.current = null;
      overlayIdsRef.current.clear();
      lastCandleCountRef.current = 0;
      lastCandleTsRef.current = 0;
      lastFirstCandleTsRef.current = 0;
      lastRightOffsetRef.current = 0;
      setChartReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // === intraday / timezone 變化 → 不重建 chart，只更新樣式 ===
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;
    chart.setStyles({
      candle: { type: isLine ? CandleType.Area : CandleType.CandleSolid },
    });
    if (timezone) chart.setTimezone(timezone);
  }, [isLine, timezone, chartReady]);

  // === 盤中分時：用自訂 intradayBaseline indicator 以「開盤」為基準上紅下綠分色；
  //     預設 area 設透明，改由該 indicator 畫（KLineCharts area 只能單色，做不到上下分色）===
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;
    if (isLine) {
      chart.setStyles({
        candle: {
          area: {
            lineColor: 'rgba(0, 0, 0, 0)',
            lineSize: 0,
            backgroundColor: [
              { offset: 0, color: 'rgba(0, 0, 0, 0)' },
              { offset: 1, color: 'rgba(0, 0, 0, 0)' },
            ],
          },
        },
      });
      chart.createIndicator({ name: INTRADAY_BASELINE_INDICATOR, extendData: { prevClose } }, true, { id: 'candle_pane' });
    } else {
      chart.removeIndicator('candle_pane', INTRADAY_BASELINE_INDICATOR);
    }
    // 成交量柱：單日折線 → 發散式、多日/日線 → 標準量柱（隨 isLine 切換）
    applyVolumeIndicator(chart, isLine, prevClose);
    // prevClose 首次值靠閉包帶入；後續變化由下方 override effect 更新（故 deps 不含 prevClose）。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLine, chartReady]);

  // === 跨日分隔線（只在多日盤中 30分/60分：intraday 但非單日折線）===
  // remove-then-create 確保 timezone 變更（切到不同市場）時 extendData 一定更新。
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;
    chart.removeIndicator('candle_pane', DAY_SEPARATOR_INDICATOR);
    if (intraday && !isLine) {
      chart.createIndicator({ name: DAY_SEPARATOR_INDICATOR, extendData: { timezone } }, true, { id: 'candle_pane' });
    }
  }, [intraday, isLine, timezone, chartReady]);

  // === 昨收基準（prevClose）/ intraday 變化 → 更新自訂 indicator 的 extendData（draw / bar styles 讀它當分色基準）===
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;
    // 分時線分色基準（此 indicator 只在單日折線模式存在）
    if (isLine) {
      chart.overrideIndicator({ name: INTRADAY_BASELINE_INDICATOR, extendData: { prevClose } }, 'candle_pane');
      // 折線模式下方 pane 是 MACD，與昨收無關，不需 override。
    } else {
      // 多日蜡燭/日線：成交量柱分色基準（收盤 vs 開盤），override 量柱 indicator。
      chart.overrideIndicator({ name: VOL_BASELINE_INDICATOR, extendData: { prevClose, intraday: isLine } }, 'volume_pane');
    }
  }, [prevClose, isLine, chartReady]);

  // === MA 期間隨 intraday 切換 ===
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;
    const maParams = intraday ? [5, 20] : [20, 60];
    chart.overrideIndicator({ name: 'MA', calcParams: maParams }, 'candle_pane');
  }, [intraday, chartReady]);

  // === data sync ===
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;
    const data = toKLineData(candles);
    if (data.length === 0) return;

    const first = data[0]!;
    const last = data[data.length - 1]!;
    const prevCount = lastCandleCountRef.current;
    const prevFirstTs = lastFirstCandleTsRef.current;
    const prevLastTs = lastCandleTsRef.current;
    const symbolChanged = symbol !== undefined && symbol !== lastSymbolRef.current;
    const intradayChanged = intraday !== lastIntradayRef.current;
    const isStructuralChange = symbolChanged || intradayChanged;
    const firstTimestampChanged = prevFirstTs !== 0 && first.timestamp !== prevFirstTs;

    const resetViewport = () => {
      const containerEl = containerRef.current;
      const width = containerEl?.clientWidth ?? 800;
      const currentPriceLines = priceLinesRef.current;
      const barSpace = resetBarSpace(data.length, width, currentPriceLines);
      const rightOffset = resetRightOffset(width, currentPriceLines);
      lastRightOffsetRef.current = rightOffset;
      chart.resize();
      chart.setBarSpace(barSpace);
      chart.setOffsetRightDistance(rightOffset);
      chart.scrollToRealTime();
      chart.resize();
      requestAnimationFrame(() => {
        if (chartRef.current !== chart) return;
        chart.setBarSpace(barSpace);
        chart.setOffsetRightDistance(rightOffset);
        chart.scrollToRealTime();
        chart.resize();
      });
      // 多階段補 resize：切股票 / 切時間框走 data-sync（非 init），若兄弟面板（quote/signal/news/
      // 體質…）此刻還在 loading→loaded、layout 抖動，上面單次 resize 會量到暫態尺寸、buffer 沒對齊
      // → K 線被垂直拉高。延遲到 layout 沉澱後再 resize 校正（修「點進場機會股票 K 線異常拉高、
      // 切時間框沒用、點別的再點回來才好」——點回來才好正是因為那時 layout 已穩、單次 resize 剛好 catch 到）。
      scheduleSettleResize(chart, containerRef.current, () => chartRef.current === chart);
    };

    const needFullReset =
      prevCount === 0 ||
      isStructuralChange ||
      data.length < prevCount ||
      data.length > prevCount + 1 ||
      firstTimestampChanged ||
      last.timestamp < prevLastTs;

    if (needFullReset) {
      chart.applyNewData(data, undefined, resetViewport);
    } else {
      chart.updateData(last);
    }

    lastCandleCountRef.current = data.length;
    lastFirstCandleTsRef.current = first.timestamp;
    lastCandleTsRef.current = last.timestamp;
    lastSymbolRef.current = symbol;
    lastIntradayRef.current = intraday;
  }, [candles, symbol, intraday, chartReady]);

  // === priceLines 同步（自訂 labeledPriceLine overlay）+ Y 軸 fit（隱藏 indicator）===
  useEffect(() => {
    if (!chartReady) return;
    const chart = chartRef.current;
    if (!chart) return;

    const next = new Map<string, PriceLineDef>();
    for (const p of priceLines ?? []) {
      if (!Number.isFinite(p.price)) continue;
      next.set(p.key, p);
    }

    // 1. 移除舊的 overlay
    for (const [key, overlayId] of overlayIdsRef.current.entries()) {
      if (!next.has(key)) {
        chart.removeOverlay({ id: overlayId });
        overlayIdsRef.current.delete(key);
      }
    }

    // 2. 新增 / 更新（簡化：每次 remove 再 add）
    for (const [key, def] of next.entries()) {
      const existing = overlayIdsRef.current.get(key);
      if (existing) {
        chart.removeOverlay({ id: existing });
        overlayIdsRef.current.delete(key);
      }
      const id = chart.createOverlay({
        name: LABELED_PRICE_LINE_OVERLAY,
        lock: true,
        points: [{ value: def.price }],
        extendData: def.title,
        styles: {
          line: {
            color: def.color,
            size: def.lineWidth ?? 1,
            style: ksLineStyle(def.lineStyle),
          },
        },
      }, 'candle_pane');
      if (typeof id === 'string') overlayIdsRef.current.set(key, id);
    }

    // 3. Y 軸 fit：把納入 autoscale 的 priceLines min/max 透過隱藏 indicator 注入主圖
    const valid = Array.from(next.values()).filter((p) => p.includeInAutoscale !== false);
    chart.removeIndicator('candle_pane', 'PRICE_BOUNDS');
    if (valid.length > 0) {
      const values = valid.map((p) => p.price);
      const minV = Math.min(...values);
      const maxV = Math.max(...values);
      // 透明 indicator：用 calc 強制把 min/max 注入 Y 軸 fit 範圍
      chart.createIndicator(
        {
          name: 'PRICE_BOUNDS',
          shortName: '',
          calcParams: [minV, maxV],
          figures: [
            { key: 'min', title: '', type: 'line' },
            { key: 'max', title: '', type: 'line' },
          ],
          calc: (dataList: KLineData[]) => dataList.map(() => ({ min: minV, max: maxV })),
          styles: {
            lines: [
              { ...SOLID_LINE_STYLE, color: 'rgba(0,0,0,0)', size: 0 },
              { ...SOLID_LINE_STYLE, color: 'rgba(0,0,0,0)', size: 0 },
            ],
          },
        } as unknown as Parameters<Chart['createIndicator']>[0],
        true,
        { id: 'candle_pane' },
      );
    }

    // priceLine 的文字已移到右側：動態保留右側 gutter，縮回原本為左側裸數字留下的空白。
    // gutter 變化時也要同步重算 barSpace，否則 6 月 / 1 年這種長區間可能又被擠出左側。
    const containerEl = containerRef.current;
    const width = containerEl?.clientWidth ?? 800;
    const nextRightOffset = resetRightOffset(width, priceLines);
    if (Math.abs(nextRightOffset - lastRightOffsetRef.current) > 8) {
      const dataCount = lastCandleCountRef.current;
      if (dataCount > 0) chart.setBarSpace(resetBarSpace(dataCount, width, priceLines));
      chart.setOffsetRightDistance(nextRightOffset);
      lastRightOffsetRef.current = nextRightOffset;
      if (dataCount > 0) chart.scrollToRealTime();
      chart.resize();
    }
  }, [priceLines, chartReady]);

  void sessionOpenTs; // 暫未使用（後端已 clip session 範圍）

  // 畫布外 OHLC strip 的顯示棒：hover 該根、否則最新根（資訊外移、不蓋 K 線）。
  const latestCandle = candles.length ? candles[candles.length - 1] : undefined;
  const srcBar = hoverBar ?? (latestCandle
    ? { timestamp: latestCandle.timestamp * 1000, open: latestCandle.open, high: latestCandle.high, low: latestCandle.low, close: latestCandle.close, volume: latestCandle.volume }
    : null);
  const stripBar = srcBar
    ? {
        time: Number(srcBar.timestamp),
        open: Number(srcBar.open),
        high: Number(srcBar.high),
        low: Number(srcBar.low),
        close: Number(srcBar.close),
        volume: Number(srcBar.volume ?? 0),
      }
    : null;
  // OHLC strip 漲跌%：日線（每根一天）顯示「該根 vs 前一交易日收盤」（當日漲跌）；盤中顯示「該根 vs 昨收」（累積漲跌）。
  // ⚠️ 日線不可用 prevClose（=meta.previousClose，在 5d/3mo/1y 是 5天前/整段起點前收）→ 會把每根都算成 vs 數天/月前、嚴重失真
  // （實測 3231.TW 日線最新根 158 vs 整段起點 129 = +22.48%，實際當日 158 vs 昨天 165.5 = -4.53%）。
  let stripPrev: number | undefined;
  if (intraday) {
    stripPrev = prevClose ?? priceLines?.find((l) => l.key === 'prev')?.price;
  } else if (stripBar) {
    const idx = candles.findIndex((c) => c.timestamp * 1000 === stripBar.time);
    stripPrev = idx > 0 ? candles[idx - 1]?.close : undefined;
  }
  const stripChg = stripBar && stripPrev != null && stripPrev > 0 ? ((stripBar.close - stripPrev) / stripPrev) * 100 : null;
  const stripChgCls = stripChg == null ? '' : stripChg > 0 ? 'text-(--up)' : stripChg < 0 ? 'text-(--down)' : 'text-(--text-muted)';
  // strip 時間格式：單日折線只顯示時間；多日盤中（30/60分）需「日期+時間」才分得出哪天；日線顯示日期。
  const barTimeMode: BarTimeMode = isLine ? 'time' : intraday ? 'datetime' : 'date';

  return (
    <div className="w-full">
      {/* 畫布外 OHLC strip：把原本蓋在 K 線上的 開/高/低/收/漲跌/量 移到這裡常駐；hover 同步該根。 */}
      <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] tnum text-(--text-muted)">
        {stripBar ? (
          <>
            <span className="text-(--text-secondary) font-semibold">{fmtBarTime(stripBar.time, barTimeMode, timezone)}</span>
            <OHLCItem label="開" value={stripBar.open} />
            <OHLCItem label="高" value={stripBar.high} cls="text-(--up)" />
            <OHLCItem label="低" value={stripBar.low} cls="text-(--down)" />
            <OHLCItem label="收" value={stripBar.close} />
            {stripChg != null && <span className={`font-bold ${stripChgCls}`}>{stripChg >= 0 ? '+' : ''}{stripChg.toFixed(2)}%</span>}
            <span>量 {fmtVol(stripBar.volume)}</span>
            {atr14 != null && Number.isFinite(atr14) && (
              <span><span className="opacity-70">ATR14</span> <span className="font-semibold text-(--text-secondary)">{atr14.toFixed(2)}</span></span>
            )}
          </>
        ) : (
          <span>—</span>
        )}
        <span className="ml-auto flex gap-3">
          <span className="ma-legend-short">— MA{intraday ? 5 : 20}</span>
          <span className="ma-legend-long">— MA{intraday ? 20 : 60}</span>
        </span>
      </div>
      <div className="relative w-full">
        <div ref={containerRef} className="w-full h-80" />
      </div>
    </div>
  );
}

function OHLCItem({ label, value, cls }: { label: string; value: number; cls?: string }) {
  return (
    <span>
      <span className="opacity-70">{label}</span>{' '}
      <span className={`font-semibold ${cls ?? 'text-(--text-primary)'}`}>{value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
    </span>
  );
}

/** 成交量緊湊格式：億 / 萬 / 原值。 */
function fmtVol(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v >= 1e8) return `${(v / 1e8).toFixed(2)}億`;
  if (v >= 1e4) return `${(v / 1e4).toFixed(1)}萬`;
  return String(Math.round(v));
}

/** strip 棒時間顯示模式：單日折線 time（HH:MM）、多日盤中 datetime（MM/DD HH:MM）、日線 date（日期）。 */
type BarTimeMode = 'time' | 'datetime' | 'date';
function fmtBarTime(ms: number, mode: BarTimeMode, timezone?: string): string {
  const tz = timezone || 'Asia/Taipei';
  const d = new Date(ms);
  if (mode === 'date') return d.toLocaleDateString('zh-TW', { timeZone: tz });
  if (mode === 'datetime') return d.toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz });
  return d.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: tz });
}
